import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { AiOutlineExport } from 'react-icons/ai';
import { HiOutlineSearch } from 'react-icons/hi';
import dayjs from 'dayjs';

import Form from 'partial/form/Form';
import { removeEmpty } from 'helper';
import Button from 'partial/components/Button';
import FormInlineInput from 'partial/form/FormInlineInput';
import FormInlineSelect from 'partial/form/FormInlineSelect';
import { useDownloadFile, useOutsideClick } from 'partial/hooks';

import { useGetRoleOptions } from '../hooks';

function UserListFilter({ onSubmit, isLoading }) {
  const exportBtnRef = useRef();
  const [showOptions, setShowOptions] = useState(false);
  const [roleOptions] = useGetRoleOptions();
  const [form, setForm] = React.useState();
  const selectedRole = React.useMemo(
    () => roleOptions?.find((c) => c.value === form?.role),
    [roleOptions, form]
  );
  const cleanListener = useOutsideClick(exportBtnRef, showOptions, () =>
    setShowOptions(false)
  );
  const [downloadCSVFile, isDownloadCSVFileLoading] = useDownloadFile({
    downloadUrl: `/acl/v1/merchant_user_roles/export?&extension=csv&keyword=${
      form?.keyword || ''
    }&role=${selectedRole?.label || ''}`,
    fileName: `ACL_MEMBER_LIST_${dayjs().format('DD/MM/YYYY')}`,
    fileExtension: 'csv',
  });
  const [downloadXLSXFile, isDownloadXLSXFileLoading] = useDownloadFile({
    downloadUrl: `/acl/v1/merchant_user_roles/export?&extension=xlsx&keyword=${
      form?.keyword || ''
    }&role=${selectedRole?.label || ''}`,
    fileName: `ACL_MEMBER_LIST_${dayjs().format('DD/MM/YYYY')}`,
    fileExtension: 'xlsx',
  });
  const [downloadPDFFile, isDownloadPDFFileLoading] = useDownloadFile({
    downloadUrl: `/acl/v1/merchant_user_roles/export?&extension=pdf&keyword=${
      form?.keyword || ''
    }&role=${selectedRole?.label || ''}`,
    fileName: `ACL_MEMBER_LIST_${dayjs().format('DD/MM/YYYY')}`,
    fileExtension: 'pdf',
  });
  const [downloadDOCXFile, isDownloadDOCXFileLoading] = useDownloadFile({
    downloadUrl: `/acl/v1/merchant_user_roles/export?&extension=docx&keyword=${
      form?.keyword || ''
    }&role=${selectedRole?.label || ''}`,
    fileName: `ACL_MEMBER_LIST_${dayjs().format('DD/MM/YYYY')}`,
    fileExtension: 'docx',
  });

  useEffect(() => cleanListener, [cleanListener]);

  return (
    <Form
      onSubmit={() =>
        onSubmit(
          removeEmpty({
            keyword: form?.keyword || '',
            role: selectedRole?.label || '',
          })
        )
      }
      className="flex items-center justify-between w-full"
    >
      <div className="flex items-center space-x-4">
        <FormInlineInput
          icon={HiOutlineSearch}
          name="keyword"
          onChange={setForm}
          value={form?.keyword || ''}
          placeholder="Search..."
        />
        <div className="w-60">
          <FormInlineSelect
            name="role"
            label="Roles"
            options={[{ label: 'All', value: '' }, ...roleOptions]}
            onChange={setForm}
            value={form?.role || ''}
          />
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <div>
          <Button
            type="button"
            outlinePrimary
            onClick={() => {
              setShowOptions(!showOptions);
            }}
            icon={AiOutlineExport}
          >
            Export as
          </Button>
          <div className="relative">
            <div
              ref={exportBtnRef}
              className={`${
                showOptions ? 'flex' : 'hidden'
              } flex-col absolute z-10 top-2 left-0 border rounded-xl w-40 bg-white shadow-lg`}
            >
              <div className="w-full px-2 pt-3 text-gray-500 hover:text-primary-800">
                <button
                  type="button"
                  className="w-full text-sm text-left"
                  onClick={downloadCSVFile}
                  disabled={isDownloadCSVFileLoading}
                >
                  <i className="fas fa-pencil-alt mr-2" />
                  .csv
                </button>
              </div>
              <div className="w-full px-2 pt-3 text-gray-500 hover:text-primary-800">
                <button
                  type="button"
                  className="w-full text-sm text-left"
                  onClick={downloadXLSXFile}
                  disabled={isDownloadXLSXFileLoading}
                >
                  <i className="fas fa-pencil-alt mr-2" />
                  .xlsx
                </button>
              </div>
              <div className="w-full px-2 pt-3 text-gray-500 hover:text-primary-800">
                <button
                  className="w-full text-sm text-left"
                  onClick={downloadPDFFile}
                  type="button"
                  disabled={isDownloadPDFFileLoading}
                >
                  <i className="fa fa-trash mr-2" />
                  .pdf
                </button>
              </div>
              <div className="w-full px-2 py-3 text-gray-500 hover:text-primary-800">
                <button
                  className="w-full text-sm text-left"
                  onClick={downloadDOCXFile}
                  type="button"
                  disabled={isDownloadDOCXFileLoading}
                >
                  <i className="fa fa-trash mr-2" />
                  .docx
                </button>
              </div>
            </div>
          </div>
        </div>
        <Button type="submit" outlinePrimary isLoading={isLoading}>
          Filter Selection
        </Button>
      </div>
    </Form>
  );
}

UserListFilter.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

UserListFilter.defaultProps = {
  isLoading: false,
};

export default UserListFilter;
